import Button from "components/button";
import ConfirmationModal from "components/shared/confirmation-modal";
import ExpandableList from "components/shared/expandable-list";
import ExportSidePanel from "components/shared/export-side-panel";
import ImportSidePanel from "components/shared/import-side-panel";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import { Table } from "components/table";
import Pagination from "components/table/pagination";
import Typography from "components/typography";

import useVendorList, {
  VendorRow,
} from "./useVendorList";

export const vendorPageRouteName =
  "/master-data/vendor";

const VendorPage = () => {
  const {
    bulkAction,
    bulkActionOptions,
    columns,
    currentPage,
    deleteRow,
    handleApplyBulkAction,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    selectedVendorNames,
    onConfirmBulkStatusUpdate,
    isExporting,
    isDeletingVendor,
    isLoading,
    isSubmittingBulkAction,
    onAddVendor,
    onConfirmDelete,
    onDraft,
    onExport,
    onRowClick,
    paginatedRows,
    rowsPerPage,
    searchValue,
    selectedKeys,
    setBulkAction,
    setCurrentPage,
    setRowsPerPage,
    setSearchValue,
    setSelectedKeys,
    handleSelectionChange,
    setShowDeleteConfirmModal,
    setSortState,
    setStatusFilter,
    showDeleteConfirmModal,
    sortState,
    statusFilter,
    t,
    totalPages,
    totalSelected,
    vendorImport,
    draftCount,
    isExportOpen,
    setIsExportOpen,
    fileFormat,
    setFileFormat,
    exportType,
    setExportType,
    exportFileFormatOptions,
    exportTypeOptions,
    handleExportSubmit,
    resultModal,
    setResultModal,
    statusToggleRow,
    showStatusConfirmModal,
    setShowStatusConfirmModal,
    onConfirmToggleStatus,
    getTranslation,
  } = useVendorList();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Typography
          variant="bodySmall"
          tone="muted"
        >
          {t(
            "selected-count",
            {
              count:
                totalSelected,
            },
          )}
        </Typography>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={
              <Icon
                name="draft"
                size={15}
              />
            }
            onClick={onDraft}
            className="relative"
            data-testid="btn-draft"
          >
            {t("btn-draft")}
            {draftCount > 0 && (
              <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 shadow-sm ring-2 ring-white">
                {draftCount}
              </span>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={
              <Icon
                name="export"
                size={15}
              />
            }
            disabled={
              isExporting
            }
            onClick={onExport}
            data-testid="btn-export-vendor"
          >
            {t("btn-export")}
          </Button>

          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={
              <Icon
                name="import"
                size={15}
              />
            }
            onClick={
              vendorImport.openModal
            }
            data-testid="btn-import-vendor"
          >
            {t("btn-import")}
          </Button>

          <Button
            type="button"
            variant="fill"
            color="primary"
            size="medium"
            iconLeft={
              <Icon
                name="add"
                size={16}
              />
            }
            onClick={onAddVendor}
            data-testid="btn-add-vendor"
          >
            {t(
              "btn-add-vendor",
            )}
          </Button>
        </div>
      </div>

      {selectedKeys.length >
        0 && (
          <div className="flex flex-col gap-3 rounded-2xl border border-border bg-white p-4 lg:flex-row lg:items-center">
            <div className="min-w-[240px]">
              <Select
                options={[
                  {
                    label: getTranslation(
                      "bulk-action-placeholder",
                      "Choose Action",
                    ),
                    value: "",
                  },
                  ...bulkActionOptions,
                ]}
                value={
                  bulkAction
                }
                onValueChange={(
                  value,
                ) =>
                  setBulkAction(
                    value as string,
                  )
                }
                disabled={bulkActionOptions.length === 0}
                placeholder={getTranslation(
                  "bulk-action-placeholder",
                  "Choose Action",
                )}
                reserveHelperSpace={
                  false
                }
              />
            </div>

            <Button
              type="button"
              variant="fill"
              color="primary"
              size="medium"
              disabled={
                isSubmittingBulkAction || !bulkAction || bulkActionOptions.length === 0
              }
              onClick={
                handleApplyBulkAction
              }
            >
              {getTranslation(
                "btn-apply",
                "Apply",
              )}
            </Button>
          </div>
        )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_220px]">
        <Input
          value={searchValue}
          onChange={(
            event,
          ) => {
            setSearchValue(
              event.target.value,
            );

            setCurrentPage(1);
          }}
          placeholder={t(
            "search-placeholder",
          )}
          leftIcon={
            <Icon
              name="search"
              size={14}
            />
          }
          reserveHelperSpace={
            false
          }
        />

        <Select
          options={[
            {
              label: t(
                "filter-status-placeholder",
              ),
              value: "",
            },
            {
              label: t(
                "status-active",
              ),
              value: "active",
            },
            {
              label: t(
                "status-inactive",
              ),
              value: "inactive",
            },
          ]}
          value={statusFilter}
          onValueChange={(
            value,
          ) => {
            setStatusFilter(
              value as string,
            );

            setCurrentPage(1);
          }}
          placeholder={t(
            "filter-status-placeholder",
          )}
          reserveHelperSpace={
            false
          }
        />
      </div>

      <section className="rounded-2xl border border-border bg-white shadow-sm">
        <Table<VendorRow>
          data={paginatedRows}
          loading={isLoading}
          columns={columns}
          rowKey="id"
          currentPage={
            currentPage
          }
          limit={rowsPerPage}
          selectable
          onRowClick={
            onRowClick
          }
          selectedRowKeys={
            selectedKeys
          }
          onSelectedRowKeysChange={(
            keys,
            rows,
          ) =>
            handleSelectionChange(
              keys,
              rows,
            )
          }
          sortState={sortState}
          onSortStateChange={
            setSortState
          }
          selectableLabel="All"
          stickySelectable
        />

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <Typography
              variant="body"
              tone="muted"
              as="span"
            >
              {t(
                "rows-per-page",
              )}
            </Typography>

            <select
              value={
                rowsPerPage
              }
              onChange={(
                event,
              ) => {
                setRowsPerPage(
                  Number(
                    event.target
                      .value,
                  ),
                );

                setCurrentPage(1);
              }}
              className="cursor-pointer rounded-lg border border-border bg-white px-2 py-1 text-sm"
            >
              <option value={10}>
                10
              </option>

              <option value={25}>
                25
              </option>

              <option value={50}>
                50
              </option>
            </select>
          </div>

          <Pagination
            currentPage={
              currentPage
            }
            totalPages={
              totalPages
            }
            onPageChange={
              setCurrentPage
            }
          />
        </div>
      </section>

      <ConfirmationModal
        open={
          showDeleteConfirmModal
        }
        title={getTranslation(
          "modal-delete-title",
          "Delete Vendor"
        )}
        message={getTranslation(
          "modal-delete-message",
          "Are you sure to delete {{name}}?",
          {
            name:
              deleteRow?.vendorName ??
              "-",
          },
        )}
        confirmLabel={getTranslation(
          "modal-ok-delete",
          "Delete"
        )}
        cancelLabel={getTranslation(
          "btn-cancel",
          "Cancel"
        )}
        onConfirm={
          onConfirmDelete
        }
        onCancel={() =>
          setShowDeleteConfirmModal(
            false,
          )
        }
        icon="danger"
        loading={isDeletingVendor}
        processingLabel={getTranslation("btn-processing", "Processing...")}
      />

      <ConfirmationModal
        open={showBulkConfirmModal}
        title={(() => {
          const status = bulkAction.toLowerCase();
          if (status === "active") return getTranslation("bulk-activate-title", "Activate Vendors");
          if (status === "inactive") return getTranslation("bulk-deactivate-title", "Deactivate Vendors");
          if (status === "delete") return getTranslation("bulk-delete-title", "Delete Vendors");
          return "";
        })()}
        message={(() => {
          const status = bulkAction.toLowerCase();
          const count = totalSelected;
          if (status === "active") {
            return count === 1
              ? getTranslation("bulk-activate-message", "Are you sure to activate 1 Vendor?", { count })
              : getTranslation("bulk-activate-message_plural", "Are you sure to activate {{count}} Vendors?", { count });
          }
          if (status === "inactive") {
            return count === 1
              ? getTranslation("bulk-deactivate-message", "Are you sure to deactivate 1 Vendor?", { count })
              : getTranslation("bulk-deactivate-message_plural", "Are you sure to deactivate {{count}} Vendors?", { count });
          }
          if (status === "delete") {
            return count === 1
              ? getTranslation("bulk-delete-message", "Are you sure to delete 1 Vendor?", { count })
              : getTranslation("bulk-delete-message_plural", "Are you sure to delete {{count}} Vendors?", { count });
          }
          return "";
        })()}
        confirmLabel={(() => {
          const status = bulkAction.toLowerCase();
          if (status === "active") return getTranslation("bulk-activate-confirm", "Activate");
          if (status === "inactive") return getTranslation("bulk-deactivate-confirm", "Deactivate");
          if (status === "delete") return getTranslation("bulk-delete-confirm", "Delete");
          return getTranslation("btn-confirm", "Confirm");
        })()}
        cancelLabel={getTranslation("btn-cancel", "Cancel")}
        onConfirm={onConfirmBulkStatusUpdate}
        onCancel={() => setShowBulkConfirmModal(false)}
        icon={bulkAction.toLowerCase() === "delete" ? "danger" : "warning"}
        loading={isSubmittingBulkAction}
        processingLabel={getTranslation("btn-processing", "Processing...")}
      >
        <ExpandableList items={selectedVendorNames} />
      </ConfirmationModal>

      <ConfirmationModal
        open={showStatusConfirmModal}
        title={(() => {
          const nextStatus = statusToggleRow?.status === "active" ? "inactive" : "active";
          return nextStatus === "active"
            ? getTranslation("modal-activate-title", "Activate Vendor")
            : getTranslation("modal-deactivate-title", "Deactivate Vendor");
        })()}
        message={(() => {
          const nextStatus = statusToggleRow?.status === "active" ? "inactive" : "active";
          const name = statusToggleRow?.vendorName ?? "";
          return nextStatus === "active"
            ? getTranslation("modal-activate-message", "Are you sure to activate {{name}}?", { name })
            : getTranslation("modal-deactivate-message", "Are you sure to deactivate {{name}}?", { name });
        })()}
        confirmLabel={(() => {
          const nextStatus = statusToggleRow?.status === "active" ? "inactive" : "active";
          return nextStatus === "active"
            ? getTranslation("btn-activate", "Activate")
            : getTranslation("btn-deactivate", "Deactivate");
        })()}
        cancelLabel={getTranslation("btn-cancel", "Cancel")}
        onConfirm={onConfirmToggleStatus}
        onCancel={() => setShowStatusConfirmModal(false)}
        icon="warning"
      />

      <ConfirmationModal
        open={resultModal.open}
        title={resultModal.title}
        message={resultModal.message}
        confirmLabel={getTranslation("btn-close", "Close")}
        hideCancelButton
        onConfirm={() => setResultModal(prev => ({ ...prev, open: false }))}
        onCancel={() => setResultModal(prev => ({ ...prev, open: false }))}
        icon={resultModal.icon}
      />

      <ImportSidePanel
        open={
          vendorImport.isOpen
        }
        title={getTranslation(
          "import-title",
          "Import Vendor",
        )}
        subtitle={getTranslation(
          "import-description",
          "Upload vendor data in Excel (.xlsx) or CSV format",
        )}
        selectedFile={
          vendorImport.selectedFile
        }
        submitLoading={
          vendorImport.isLoading
        }
        onClose={
          vendorImport.closeModal
        }
        onFileSelect={
          vendorImport.onSelectFile
        }
        onFileRemove={
          vendorImport.removeFile
        }
        onSubmit={
          vendorImport.submitImport
        }
        onTemplateDownload={
          vendorImport.downloadTemplate
        }
        acceptedFileTypes=".xlsx,.xls,.csv"
        templateFileName="Vendor Import Template.xlsx"
      />

      <ExportSidePanel
        open={isExportOpen}
        onClose={() =>
          setIsExportOpen(
            false,
          )
        }
        onSubmit={
          handleExportSubmit
        }
        fileFormatOptions={
          exportFileFormatOptions
        }
        exportTypeOptions={
          exportTypeOptions
        }
        fileFormatValue={
          fileFormat
        }
        exportTypeValue={
          exportType
        }
        onFileFormatChange={
          setFileFormat
        }
        onExportTypeChange={
          setExportType
        }
        submitLoading={isExporting}
        title={getTranslation("export-title", "Export Vendor")}
        subtitle={getTranslation("export-description", "Download your vendor data in your preferred format")}
      />
    </div>
  );
};

export default VendorPage;